import { onBeforeUnmount, onMounted } from 'vue';
import { onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router';
import {
  getGeneratedRecordForm,
  GENERATED_RECORD_HOLD_EVENT,
  GENERATED_RECORD_LOCK_LOST_EVENT,
  GENERATED_RECORD_UNHOLD_EVENT,
  isGeneratedRecordLockEvent,
  isHoldAccepted,
  openGeneratedRecord,
  releaseGeneratedRecord,
  releaseGeneratedRecordOnUnload,
  type OpenGeneratedRecordResult,
} from '@/entities/GeneratedRecord';
import { useAuthStore } from '@/entities/Session';
import { emitRealtimeEvent, onRealtimeEvent } from '@/shared/api';

interface HeldLock {
  id: number;
  token: string;
  reclaimed?: boolean;
}

interface RecordLockOptions {
  onRestoredFromCache?: () => void;
  // The server freed the lock (a lost connection or, later, a superuser): the sheet stays as typed.
  onLost?: () => void;
  // The lock came back after a connection that dropped for longer than the grace period.
  onReclaimed?: (opened: OpenGeneratedRecordResult) => void;
}

export function useRecordLock(options: RecordLockOptions = {}) {
  const authStore = useAuthStore();

  // Which record and which token, not merely whether one is held: the router keeps this screen
  // alive when only the id changes, so by the time a guard runs the route already names the next
  // record, and the server only frees a lock for the token that took it.
  let held: HeldLock | null = null;
  let latestOpen = 0;
  let unregisterSessionHook: (() => void) | null = null;
  let unsubscribers: Array<() => void> = [];

  // Over the socket as well as over HTTP: the app socket outlives this screen, so a release call
  // that failed would otherwise leave the server believing the lock is still held here.
  function declareUnhold(lock: HeldLock): void {
    emitRealtimeEvent(GENERATED_RECORD_UNHOLD_EVENT, {
      generatedRecordId: lock.id,
      inUseToken: lock.token,
    });
  }

  async function releaseQuietly(lock: HeldLock): Promise<void> {
    declareUnhold(lock);
    try {
      await releaseGeneratedRecord(lock.id, { inUseToken: lock.token });
    } catch {
      // Trapping the person on this screen because a cleanup call failed is worse than a held
      // lock, which the server frees once the connection is gone.
    }
  }

  async function acquire(
    id: number,
    { reclaimed }: { reclaimed: boolean }
  ): Promise<OpenGeneratedRecordResult | null> {
    const request = ++latestOpen;
    const opened = await openGeneratedRecord(id);

    if (request !== latestOpen) {
      // The person left while the lock was being taken; nobody else will ever free this token.
      void releaseQuietly({ id, token: opened.inUseToken });
      return null;
    }

    held = { id, token: opened.inUseToken, reclaimed };
    declareHold();
    return opened;
  }

  function open(id: number): Promise<OpenGeneratedRecordResult | null> {
    return acquire(id, { reclaimed: false });
  }

  // Tells the server which socket holds the lock, so a connection that never comes back frees it.
  // Every new socket declares again; a refusal means the server already freed this token.
  function declareHold(): void {
    const lock = held;
    if (!lock) return;
    emitRealtimeEvent(
      GENERATED_RECORD_HOLD_EVENT,
      { generatedRecordId: lock.id, inUseToken: lock.token },
      (reply) => {
        if (held !== lock) return;
        if (isHoldAccepted(reply)) {
          lock.reclaimed = false;
          return;
        }
        // One reopen per lost lock: a server that refuses the reopened lock too (a socket bound to
        // another branch than the cookie) would otherwise be asked again without end. An accepted
        // hold ends that loss, so a later drop may reopen again.
        if (lock.reclaimed) {
          held = null;
          options.onLost?.();
          return;
        }
        void reclaim(lock);
      }
    );
  }

  async function reclaim(lost: HeldLock): Promise<void> {
    held = null;
    const request = latestOpen;
    try {
      // Another tab of the same member may hold the record by now, and the open lets the same
      // member take it again, rotating that tab's token: only a free record is taken back.
      const current = await getGeneratedRecordForm(lost.id);
      if (request !== latestOpen) return;
      if (current.record?.status !== 'AVAILABLE') {
        options.onLost?.();
        return;
      }
      const opened = await acquire(lost.id, { reclaimed: true });
      if (opened) options.onReclaimed?.(opened);
    } catch {
      options.onLost?.();
    }
  }

  function loseLock(event: unknown): void {
    if (!held || !isGeneratedRecordLockEvent(event) || event.generatedRecordId !== held.id) return;
    if (event.inUseToken !== undefined && event.inUseToken !== held.token) return;
    held = null;
    options.onLost?.();
  }

  function token(): string | null {
    return held?.token ?? null;
  }

  async function release(): Promise<void> {
    latestOpen += 1;
    const lock = held;
    held = null;
    if (lock) await releaseQuietly(lock);
  }

  // The server already freed it: the save released it in its own transaction, or the lock was lost.
  // Only the acquisition that token names: a late answer for the previous record must not drop the
  // lock the screen took for the next one.
  function forget(token: string): void {
    if (held?.token === token) held = null;
  }

  function releaseOnPageHide(): void {
    latestOpen += 1;
    const lock = held;
    held = null;
    if (!lock) return;
    declareUnhold(lock);
    releaseGeneratedRecordOnUnload(lock.id, { inUseToken: lock.token });
  }

  function reopenOnPageShow(event: PageTransitionEvent): void {
    if (event.persisted) options.onRestoredFromCache?.();
  }

  onBeforeRouteLeave(release);
  // A change of only the id reuses this screen, and vue-router calls leave guards only for the
  // records being left: without this, the previous record would stay locked.
  onBeforeRouteUpdate(release);

  onMounted(() => {
    window.addEventListener('pagehide', releaseOnPageHide);
    window.addEventListener('pageshow', reopenOnPageShow);
    unregisterSessionHook = authStore.onBeforeSessionChange(release);
    unsubscribers = [
      onRealtimeEvent('connect', declareHold),
      onRealtimeEvent(GENERATED_RECORD_LOCK_LOST_EVENT, loseLock),
    ];
  });

  onBeforeUnmount(() => {
    window.removeEventListener('pagehide', releaseOnPageHide);
    window.removeEventListener('pageshow', reopenOnPageShow);
    unregisterSessionHook?.();
    unsubscribers.forEach((unsubscribe) => unsubscribe());
    void release();
  });

  return { open, token, release, forget };
}
